import React, { useEffect, useMemo, useState } from "react";
import { api, urls } from "../../api/api";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import PaginationBar from "../../components/Common/PaginationBar";

const Agents = () => {
  document.title = "Agents";

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  // search + pagination
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    is_active: true,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(urls.agents);
      setAgents(Array.isArray(r.data) ? r.data : []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh().catch((e) => alert(e?.response?.data?.message || "Erreur chargement agents"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAgents = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return agents;
    return agents.filter((a) => {
      const name = (a.name || "").toLowerCase();
      const email = (a.email || "").toLowerCase();
      return name.includes(s) || email.includes(s);
    });
  }, [agents, q]);

  // pagination
  const totalItems = filteredAgents.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedAgents = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredAgents.slice(start, start + pageSize);
  }, [filteredAgents, safePage, pageSize]);

  const toggle = () => {
    setIsOpen((v) => !v);
    if (isOpen) {
      setEditingId(null);
      setForm({ name: "", email: "", password: "", is_active: true });
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: "", email: "", password: "", is_active: true });
    setIsOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || "",
      email: a.email || "",
      password: "",
      is_active: !!a.is_active,
    });
    setIsOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return alert("Nom obligatoire");
    if (!form.email.trim()) return alert("Email obligatoire");

    try {
      if (editingId) {
        await api.put(`${urls.agents}/${editingId}`, {
          name: form.name,
          email: form.email,
          is_active: form.is_active,
          ...(form.password ? { password: form.password } : {}),
        });
      } else {
        if (!form.password) return alert("Password obligatoire (création)");
        await api.post(urls.agents, {
          name: form.name,
          email: form.email,
          password: form.password,
        });
      }

      toggle();
      await refresh();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join("\n") : null) ||
        "Erreur sauvegarde";
      alert(msg);
    }
  };

  const disableAgent = async (id) => {
    if (!window.confirm("Désactiver cet agent ?")) return;
    try {
      await api.delete(`${urls.agents}/${id}`);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur");
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div style={{ width: 420, maxWidth: "100%" }}>
                  <Input
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Recherche agent..."
                  />
                </div>

                <div className="d-flex gap-2">
                  <Button color="secondary" onClick={refresh}>
                    Refresh
                  </Button>
                  <Button color="primary" onClick={openAdd}>
                    + Ajouter agent
                  </Button>
                </div>
              </div>

              <div className="table-responsive">
                <Table className="table align-middle table-nowrap">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Actif</th>
                      <th style={{ width: 180 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4">Chargement...</td>
                      </tr>
                    ) : paginatedAgents.length === 0 ? (
                      <tr>
                        <td colSpan="4">Aucun agent</td>
                      </tr>
                    ) : (
                      paginatedAgents.map((a) => (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          <td>{a.email}</td>
                          <td>{a.is_active ? "Oui" : "Non"}</td>
                          <td>
                            <Button size="sm" color="info" className="me-2" onClick={() => openEdit(a)}>
                              Modifier
                            </Button>
                            <Button size="sm" color="danger" disabled={!a.is_active} onClick={() => disableAgent(a.id)}>
                              Désactiver
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Modifier Agent" : "Ajouter Agent"}</ModalHeader>

        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Nom</Label>
              <Input name="name" value={form.name} onChange={onChange} />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input name="email" value={form.email} onChange={onChange} />
            </FormGroup>

            <FormGroup>
              <Label>Password {editingId ? "(optionnel)" : ""}</Label>
              <Input
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                placeholder={editingId ? "Laisser vide si pas de changement" : ""}
              />
            </FormGroup>

            {editingId && (
              <FormGroup check className="mt-2">
                <Input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                <Label check className="ms-2">
                  Actif
                </Label>
              </FormGroup>
            )}
          </Form>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Fermer
          </Button>
          <Button color="primary" onClick={save}>
            Enregistrer
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Agents;
